import { useEffect, RefObject } from "react";

import { coalesce, doNothing } from "@kazoohr/helpers";

import { onBrowser } from "./onBrowser";

/**
 * React hook to provide a handler for outside click events. Can be used to
 * close dropdowns, modals, etc. when user clicks outside of the element.
 */
export function useOutsideClick({
  elementRefs = [],
  dependencies = [],
  ignoreEffect = false,
  handler = doNothing,
}: {
  /**
   * List of refs to elements that should be ignored when clicked
   */
  elementRefs: RefObject<HTMLElement>[];
  /**
   * List of dependencies for the effect
   */
  dependencies?: any[];
  /**
   * Flag to ignore the effect. If true, we won't trigger the outside click handler. Useful for fine-grained control for user interactions.
   */
  ignoreEffect?: boolean;
  /**
   * Function to be called when user clicks outside of the element
   */
  handler?: () => void;
}) {
  useEffect(() => {
    if (ignoreEffect || !onBrowser()) {
      return;
    }

    if (!getWindow()[awayClickBrowserProp]) {
      getWindow()[awayClickBrowserProp] = new Map();
    }

    const clickAwayNodes = getWindow()[awayClickBrowserProp];
    clickAwayNodes?.set(elementRefs, handler);
    document.addEventListener("click", outsideClickHandler, true);
    document.addEventListener("tap", outsideClickHandler, true);

    return () => {
      clickAwayNodes?.delete(elementRefs);
      document.removeEventListener("click", outsideClickHandler, true);
      document.removeEventListener("tap", outsideClickHandler, true);
    };
  }, [ignoreEffect, ...dependencies]);

  return;
}

function outsideClickHandler(event: unknown) {
  // istanbul ignore next - no need to test this
  if (!onBrowser()) {
    return;
  }
  // istanbul ignore next - conditional here to ensure runtime safety, not necessary to test the null case
  const target: any =
    event && typeof event === "object" && "target" in event && event.target
      ? event.target
      : null;
  const clickAwayNodes = coalesce(getWindow()[awayClickBrowserProp], []);
  for (const [nodes, fn] of clickAwayNodes) {
    const insideNode = nodes.some(
      (node) => node.current && target && node.current.contains(target)
    );
    if (!insideNode && fn) {
      fn();
    }
  }
}

const awayClickBrowserProp = "$confettiClickAwayNodes" as const;

function getWindow(): {
  [awayClickBrowserProp]?: Map<RefObject<HTMLElement>[], () => void>;
} {
  // This is a safe assertion, since we know `window` is a global object.
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return window as any;
}
